import { useState } from "react";
import useAuth from "../hooks/useAuth";
import { addUserAcknowledgement } from "../../services/acknowledgement"; 
import { ACKNOWLEDGEMENT_TYPE_TNC, ACKNOWLEDGEMENT_TYPE_DECLARATION } from "../../services/Constants";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import { RenderTncContent } from "./RenderTncContent";

const AcknowledgementsPage = () => {
	const { 
		setUserSignedTncVersion, 
		setUserSignedDeclarationTime,
		getToken, 
		latestTncVersion,
		shouldUserSignTnc,
		shouldUserProvideDeclaration
	} = useAuth();

	const [tncChecked, setTncChecked] = useState(false);
	const [declarationChecked, setDeclarationChecked] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmitAcknowledgements = async () => {
        
        try {
            setIsSubmitting(true);
			const tokenObj = getToken();
			if (tokenObj?.token) {
                if ((shouldUserSignTnc && !tncChecked) || (shouldUserProvideDeclaration && !declarationChecked)) {
                    toast.error("Please accept all pending acknowledgements.", { autoClose: 4000 });
                    setIsSubmitting(false);
                    return;
                }
                toast.info("Submitting acknowledgements.", { autoClose: 4000 });
				var signTnCResponse, declarationResponse;
				var userSignedTnCVersion = "", userDeclaration = "";
                // Submit T&C if needed
                if (shouldUserSignTnc) {
                    signTnCResponse = await addUserAcknowledgement(
                        tokenObj.token,
                        ACKNOWLEDGEMENT_TYPE_TNC,
                        latestTncVersion,
                        ""
                    );
					if (signTnCResponse.data?.addUserAcknowledgement) {
						userSignedTnCVersion = latestTncVersion;
						toast.success("T&C acknowledged successfully.", { autoClose: 4000 });
					} else {
						throw new Error("Error submitting T&C acknowledgement");
					}
                }

                // Submit Declaration if needed
				const todayDate = new Date().toISOString();
                if (shouldUserProvideDeclaration) {
                    declarationResponse = await addUserAcknowledgement(
                        tokenObj.token,
                        ACKNOWLEDGEMENT_TYPE_DECLARATION,
                        todayDate.slice(0, 10).replace(/-/g, ""), // e.g., "20250427"
                        ""
                    );
					if (shouldUserProvideDeclaration && declarationResponse.data?.addUserAcknowledgement) {
						userDeclaration = todayDate;
						toast.success("Declaration submitted successfully.", { autoClose: 4000 });
					} else {
						throw new Error("Error submitting Declaration acknowledgement");
					}
                }
				shouldUserSignTnc && setUserSignedTncVersion(userSignedTnCVersion);
				shouldUserProvideDeclaration && setUserSignedDeclarationTime(userDeclaration);
			}
		} catch (error) {
			console.error("Failed to submit acknowledgements", error);
			toast.error("Something went wrong while submitting acknowledgements. Please try after some time or contact support for help.", { autoClose: 4000 });
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<ToastContainer />
			<Box maxWidth="md" mx="auto" p={4}>
				<Typography variant="h4" fontWeight="bold" gutterBottom>
					Acknowledgements
				</Typography>

				<Typography variant="body1" color="textSecondary" gutterBottom>
					To help maintain a safe, transparent, and trustworthy community, 
					we periodically request your acknowledgment of important terms and information. 
					Please review the following carefully to stay informed and continue using the platform with confidence.
				</Typography>
			

				{shouldUserSignTnc && (
					<Box
						border={1}
						borderRadius={2}
						borderColor="grey.300"
						p={3}
						mb={4}
						maxHeight="60vh"
						overflow="auto"
						bgcolor="grey.50"
					>
						<Typography variant="h5" fontWeight="medium" gutterBottom>
							Terms and Conditions
						</Typography>
						<RenderTncContent />
					</Box>
				)}

				{shouldUserSignTnc && (
					<FormControlLabel
						control={
							<Checkbox
								checked={tncChecked}
								onChange={() => setTncChecked(!tncChecked)}
								color="primary"
								disabled={isSubmitting}
							/>
						}
						label="I have read and understood the Terms and Conditions"
					/>
				)}

				{shouldUserProvideDeclaration && (
					<FormControlLabel
						control={
							<Checkbox
								checked={declarationChecked}
								onChange={() => setDeclarationChecked(!declarationChecked)}
								color="primary"
								disabled={isSubmitting}
							/>
						}
						label="I confirm that the information I have submitted is true and accurate"
					/>
				)}

				<Box mt={4}>
					<Button
						variant="contained"
						color="primary"
						size="large"
						disabled={isSubmitting}
						onClick={handleSubmitAcknowledgements}
						startIcon={isSubmitting && <CircularProgress size={20} />}
					>
						{isSubmitting ? "Submitting..." : "Provide Acknowledgements"}
					</Button>
				</Box>
			</Box>
		</>
	);
};

export default AcknowledgementsPage;
